import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { MutationStateFns } from '@/api/utils/mutation';
import { useAuth } from '@/context/AuthProvider';

export type ApiRequest_SendInviteEmails = {
  community_id: number;
  negotiation_id: number;
  shareable_link: string;
  emails: string[];
};

export type ApiResponse_SendInviteEmails = {
  success: boolean;
  sent: number;
  failed: number;
  total: number;
  message: string;
};

const METHOD = 'POST';
const ENDPOINT = 'negotiation/send-invite-emails';

export const useSendInviteEmails = (
  stateFns?: MutationStateFns<ApiResponse_SendInviteEmails>
) => {
  const { session } = useAuth();

  return useMutation({
    mutationFn: async (data: ApiRequest_SendInviteEmails) =>
      handleApiCall<ApiResponse_SendInviteEmails, ApiRequest_SendInviteEmails>(
        session,
        METHOD,
        getResolvedEndpoint(ENDPOINT, {}),
        data
      ),
    onSuccess: (data) => {
      if (data.sent > 0) {
        enqueueSnackbar(
          `Successfully sent ${data.sent} invitation${data.sent > 1 ? 's' : ''}`,
          { variant: 'success' }
        );
      }
      if (data.failed > 0) {
        enqueueSnackbar(
          `Failed to send ${data.failed} invitation${data.failed > 1 ? 's' : ''}`,
          { variant: 'error' }
        );
      }
      stateFns?.onSuccess?.(data);
    },
    onError: (err: AxiosError) => {
      enqueueSnackbar('Failed to send email invitations', { variant: 'error' });
      stateFns?.onError?.(err);
    },
  });
};